/**
 * What became of one submission.
 *
 * A settings form used to answer 「已保存」 as soon as the core accepted the
 * message. That is a claim about the wrong thing: accepted is not done, and a
 * tool that failed afterwards said nothing at all. This shows the two facts
 * that exist -- the obligation's state, and every effect row it opened -- and
 * says "unknown" where the ledger has not spoken yet rather than filling in a
 * cheerful default.
 *
 * Read once on mount and then told (CHARTER §7.194): the server pushes a
 * `command` frame when the status moves. It used to ask again every 400 ms.
 */

import { useEffect, useRef } from "react";
import { heldKeys, useCommandStatus } from "../lib/commandFeed";
import { usePendingApprovals } from "../lib/approvalsFeed";
import { StateBadge, StatusBadge } from "../shell/ui";
import { responseConversationHref } from "../shell/ownerShell";
import { failureWords } from "../studio/words";

// Request outcomes, including work resumed after approval.
const SETTLED = new Set(["completed", "failed", "unknown", "stopped"]);

export function CommandTrail({
  label,
  obligationId,
  onDone,
  ownerMode = false,
  active = true,
}: {
  label: string;
  obligationId: string;
  onDone?: () => void;
  ownerMode?: boolean;
  sessionId?: string;
  active?: boolean;
}) {
  const { status } = useCommandStatus(obligationId, active);
  const holding = heldKeys(status);
  const { pending } = usePendingApprovals(ownerMode && active && status?.action_state === "held" && holding.length > 0);
  const approval = pending?.find(card => holding.includes(card.key) || (card.effect_id && holding.includes(`effect:${card.effect_id}`)));
  // A caller's page context cannot name the conversation holding this command.
  // Only the matched approval supplies that; an unknown source opens the Owner's conversation.
  const approvalHref = approval ? responseConversationHref(approval, { approval: approval.key })
    : responseConversationHref({ session_id: "s-owner" }, holding[0] ? { approval: holding[0] } : {});
  // `onDone` is a fresh closure on every render of the parent; it is called
  // once per settled obligation, whichever closure is current then.
  const done = useRef(onDone);
  done.current = onDone;
  const settled = useRef<string | null>(null);
  useEffect(() => {
    // A turn commits while its approval is still waiting. Follow the action
    // through its continuation before saying it ended.
    if (!status?.action_state || !SETTLED.has(status.action_state)) return;
    if (settled.current === obligationId) return;
    settled.current = obligationId;
    done.current?.();
  }, [status, obligationId]);

  const state = status?.state ?? null;
  if (ownerMode) {
    const action = status?.action_state;
    const labelText =
      action === "held"
        ? "等待确认"
        : action === "failed"
          ? "未能完成"
          : action === "completed"
            ? "执行已完成"
            : action === "stopped"
              ? "已停下"
              : action === "unknown"
                ? "结果尚不确定"
                : "正在处理";
    return (
      <div className="command-trail" data-testid="command-trail" role="status">
        <div className="trail-line">
          <span>{label}</span>
          <StatusBadge
            tone={
              action === "failed"
                ? "critical"
                : action === "held" || action === "unknown"
                  ? "warning"
                  : "neutral"
            }
            label={labelText}
          />
        </div>
        {status?.error ? <p className="micro" role="alert">{failureWords(status.error)}</p> : null}
        {action === "held" ? (
          <a href={approvalHref}>{approval ? "在对话中确认" : "在对话中查看待确认事项"}</a>
        ) : null}
      </div>
    );
  }
  return (
    <div className="command-trail" data-testid="command-trail" role="status">
      <div className="trail-line">
        <span className="micro">{label}</span>
        <span className="micro">
          欠条 <code className="mono">{obligationId}</code>
        </span>
        <StateBadge state={state} />
      </div>
      {status?.effects.length ? (
        <ul className="trail-effects">
          {status.effects.map((effect) => (
            <li key={effect.id}>
              <code className="mono">{effect.tool ?? "未知"}</code>
              <StateBadge state={effect.state} />
              {effect.error ? (
                <span className="micro">{effect.error}</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="micro faint">
          还没有效果行。有工具真的动手，这里才多一行。
        </p>
      )}
    </div>
  );
}
